import React, { ChangeEvent, forwardRef, ReactNode, Ref, useEffect, useRef, useState } from 'react';
import InputMask from 'react-input-mask';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import minus from '../../../../utils/minus';
import { Badge } from '../../Badge';
import { FS_PRIVACY_MASK_CLASS, getClassName, InputTextType, TextTransform } from '../../helpers/InputBase';
import { CaptionStyles, FieldStyles, FieldWrapperStyles, InputGroupStyles, InputLabelStyles, InputTextStyles } from './styles';
interface Props {
  /** The autoComplete attribute for the input */
  autoComplete?: string;
  /** Pass through classname to allow styles overrides */
  className?: string;
  /** Caption message to display below the input */
  caption?: ReactNode;
  /** Used to identify the input */
  dataId?: string;
  /** An object of custom props that is not explicitly supported, but might be required for some custom implementations.
   * This can also be used to override the explicit props, like a custom onChange function.
   * or as a way to get rid of the chrome autofill.
   * */
  // eslint-disable-next-line
  customProps?: Record<string, any>;
  /** Whether the input has an error */
  hasError?: boolean;
  /** Used to link the input with the label when the label is clicked */
  id: string;
  /** Put the input in the disabled state */
  disabled?: boolean;
  /** Put the input in the readonly state */
  isReadOnly?: boolean;
  /** Add an asterisk to the label */
  isRequired?: boolean;
  /** Put the input in the valid state */
  isValid?: boolean;
  /** Put the input in the invalid state */
  isInvalid?: boolean;
  /** Whether the input has sensitive data and should be masked in FullStory */
  isPrivate?: boolean;
  /** Label text that appears above the input field. Will always be displayed instead of placeholder if both are provided */
  label?: string;
  /** The mask used to format the user input */
  mask?: string;
  /** The name of the input */
  name: string;
  /** An onBlur callback passed down by the controlling parent component */
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  /** Callback function that is called when the input value changes */
  onChange?: (value: string, event: ChangeEvent) => void;
  /** Callback function that is called when the input is focused */
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  /** Display a placeholder when the input is empty and the label is not provided. */
  placeholder?: string;
  /** Any component to display before the input */
  prefix?: ReactNode;
  /** Any component to display after the input */
  suffix?: ReactNode;
  /** CSS text-transform style to apply to the input field */
  textTransformType?: TextTransform;
  /** The type of the input */
  type?: InputTextType;
  /** The value of the input */
  value?: string;
}
const _exp = /*#__PURE__*/() => ({
  prefixWidth
}) => `translateX(${minus(`${prefixWidth + 10}px`)}) translateY(-18px)`;
const _exp2 = /*#__PURE__*/() => ({
  prefixWidth
}) => `translateX(${prefixWidth + 10}px) translateY(-18px)`;
const Field = /*#__PURE__*/styled('label')({
  name: "Field",
  class: "fgfs5m6",
  propsAsIs: false,
  vars: {
    "fgfs5m6-0": [_exp()],
    "fgfs5m6-1": [_exp2()]
  }
});
const _exp3 = /*#__PURE__*/() => ({
  textTransformType
}) => textTransformType ? textTransformType : TextTransform.none;
const Input = /*#__PURE__*/styled('input')({
  name: "Input",
  class: "izpge60",
  propsAsIs: false,
  vars: {
    "izpge60-0": [_exp3()]
  }
});
export const PrefixWrapperStyles = "pveewno";
export const InputText = forwardRef<HTMLInputElement, Props>(({
  id,
  name,
  type = InputTextType.text,
  autoComplete,
  className,
  dataId,
  label,
  placeholder,
  value,
  caption,
  prefix,
  suffix,
  isValid = false,
  isInvalid = false,
  disabled = false,
  isRequired = false,
  isReadOnly = false,
  isPrivate = false,
  onChange,
  onFocus,
  onBlur,
  textTransformType,
  mask,
  customProps
}: Props, ref: Ref<HTMLInputElement>) => {
  const [isFilled, setIsFilled] = useState(false);
  const [prefixWidth, setPrefixWidth] = useState(0);
  const prefixRef = useRef<HTMLDivElement>(null);
  const handleChange = (event: ChangeEvent) => {
    const {
      value: newValue
    } = event.currentTarget as HTMLInputElement;
    onChange?.(newValue, event);
  };
  useEffect(() => {
    setIsFilled(Boolean(value?.length));
  }, [value]);
  useEffect(() => {
    if (prefix && prefixRef.current) {
      setPrefixWidth(prefixRef.current.offsetWidth);
    }
  }, [prefix, prefixRef?.current?.offsetHeight]);
  const commonProps = {
    autoComplete,
    id,
    disabled,
    maskChar: null,
    name,
    textTransformType,
    type,
    value,
    placeholder,
    label,
    onBlur,
    onFocus,
    onChange: handleChange,
    readOnly: isReadOnly,
    ['data-id']: dataId,
    ['data-field-error']: isInvalid
  };
  return <div className={cx(FieldWrapperStyles, className)}>
        <Field prefixWidth={prefixWidth} htmlFor={id} className={cx(FieldStyles, isInvalid && '__isInvalid', isFilled && '__isFilled', disabled && '__isDisabled', isReadOnly && '__isReadOnly', prefix && '__hasPrefix', label && '__hasLabel')}>
          {prefix && <div className={cx(PrefixWrapperStyles)} ref={prefixRef}>
              {prefix}
            </div>}

          <div className={InputGroupStyles}>
            {mask ? <InputMask inputRef={ref} mask={mask} {...customProps} {...commonProps} className={cx(InputTextStyles, getClassName(isPrivate))} /> : <Input ref={ref} {...commonProps} {...customProps} className={cx(InputTextStyles, isPrivate && FS_PRIVACY_MASK_CLASS)} />}
            {label && <span className={cx(InputLabelStyles, 'label')}>
                {label}
                {isRequired && <span className="asterisk">*</span>}
              </span>}
          </div>

          {isValid && <Badge size="xs" variant="success" iconName="Checkmark" />}

          {isInvalid && <Badge size="xs" variant="danger" iconName="WarningMark" />}

          {suffix && <div>{suffix}</div>}
        </Field>

        {caption && <div className={cx(CaptionStyles, isInvalid && '__isInvalid', disabled && '__isDisabled')}>
            {caption}
          </div>}
      </div>;
});
InputText.displayName = 'InputText';

require("./InputText.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./InputText.tsx");